import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // If a force-light page saved the real preference (e.g. user refreshed
    // on the sign-in page), the stored theme will be 'light' but the user
    // actually prefers dark. We still start as 'light' here — the real
    // preference is restored when navigating away from the force-light page.
    const stored = localStorage.getItem('clinlexis_theme') as Theme;
    if (stored) return stored;

    return 'light';
  });

  useEffect(() => {
    // Apply theme to document
    const root = window.document.documentElement;
    
    // Remove both dark and light classes first to ensure clean state
    root.classList.remove('dark', 'light');
    
    // Add the appropriate theme class
    if (theme === 'dark') {
      root.classList.add('dark');
    }
    // For light mode, we don't add any class (Tailwind default is light)
    
    // Store preference
    localStorage.setItem('clinlexis_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
